import {
  compileNewsBlocklist,
  loadNewsPreferences,
  type NewsPreferences,
} from '../utils/newsPreferences';
import {
  filterRedditPosts,
  type RedditFeedPost,
} from '../utils/redditTitleFilter';

const ATOM_NS = 'http://www.w3.org/2005/Atom';

export type SubredditFeed = {
  subreddit: string;
  posts: RedditFeedPost[];
  error?: string;
};

function redditRssUrl(subreddit: string, limit: number): string {
  const sub = encodeURIComponent(subreddit);
  return `/api/reddit/r/${sub}/hot.rss?limit=${limit}`;
}

export function parseRedditAtomFeed(xml: string, subreddit: string): RedditFeedPost[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid RSS XML from Reddit');
  }

  const entries = doc.getElementsByTagNameNS(ATOM_NS, 'entry');
  const posts: RedditFeedPost[] = [];

  for (let i = 0; i < entries.length; i++) {
    const entry = entries.item(i);
    if (!entry) continue;
    const title = entry.getElementsByTagNameNS(ATOM_NS, 'title').item(0)?.textContent?.trim();
    if (!title) continue;
    const linkEl =
      entry.querySelector('link[rel="alternate"]') ??
      entry.getElementsByTagNameNS(ATOM_NS, 'link').item(0);
    const href = linkEl?.getAttribute('href')?.trim() ?? '';
    posts.push({
      title: title.replace(/\s+/g, ' '),
      link: href,
      subreddit,
    });
  }

  return posts;
}

export async function fetchSubredditHot(
  subreddit: string,
  limit: number,
  signal?: AbortSignal
): Promise<RedditFeedPost[]> {
  const init: RequestInit = {
    headers: {
      Accept: 'application/atom+xml,application/xml;q=0.9,*/*;q=0.8',
    },
  };
  if (signal) init.signal = signal;
  const res = await fetch(redditRssUrl(subreddit, limit), init);
  if (!res.ok) {
    throw new Error(`Reddit returned ${res.status}`);
  }
  const xml = await res.text();
  return parseRedditAtomFeed(xml, subreddit);
}

export async function fetchNewsFeeds(
  prefs: NewsPreferences = loadNewsPreferences(),
  signal?: AbortSignal
): Promise<SubredditFeed[]> {
  const blocklist = compileNewsBlocklist(prefs);
  const limit = Math.min(25, prefs.maxPostsPerSub + 5);

  const results = await Promise.all(
    prefs.subreddits.map(async subreddit => {
      try {
        const raw = await fetchSubredditHot(subreddit, limit, signal);
        const posts = filterRedditPosts(raw, blocklist.patterns).slice(0, prefs.maxPostsPerSub);
        return { subreddit, posts };
      } catch (err) {
        return {
          subreddit,
          posts: [],
          error: (err as Error).message,
        };
      }
    })
  );

  return results;
}

/** For tests / debugging */
export function applyNewsFilters(
  posts: readonly RedditFeedPost[],
  prefs: NewsPreferences
): RedditFeedPost[] {
  const blocklist = compileNewsBlocklist(prefs);
  return filterRedditPosts(posts, blocklist.patterns).slice(0, prefs.maxPostsPerSub);
}
